import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEO from './components/SEO';

const milestones = [
  { year: '2018', title: '平台诞生', desc: '蜜桃视频正式上线，以"发现精彩，记录生活"为使命，开启短视频创作分享之旅。' },
  { year: '2019', title: '用户突破100万', desc: '凭借优质内容和极佳用户体验，注册用户数快速增长至百万级别。' },
  { year: '2020', title: '创作者生态建立', desc: '推出创作者激励计划，为优秀创作者提供流量扶持与变现渠道。' },
  { year: '2021', title: '内容多元化', desc: '拓展至八大内容分类，涵盖生活、美食、娱乐、创意等全领域内容。' },
  { year: '2022', title: '用户突破5000万', desc: '月活跃用户突破5000万，日视频播放量超过2亿次。' },
  { year: '2023', title: 'AI赋能创作', desc: '引入AI智能推荐和创作辅助工具，提升内容分发效率与创作体验。' },
  { year: '2024', title: '全球化布局', desc: '开始拓展海外市场，支持多语言内容，服务全球用户和创作者。' },
];

const testimonials = [
  {
    name: '甜蜜厨房',
    role: '美食创作者',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20female%20chef%20portrait%2C%20warm%20smile%2C%20clean%20white%20chef%20apron%2C%20soft%20studio%20lighting%2C%20neutral%20cream%20background%2C%20professional%20headshot%20style&width=120&height=120&seq=author-avatar-01&orientation=squarish',
    quote: '蜜桃视频让我找到了与粉丝分享美食热情的最佳方式。平台的创作者支持体系非常完善，让我能专注于内容创作本身。',
  },
  {
    name: '行走的咖啡',
    role: '旅行创作者',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20male%20travel%20blogger%20portrait%2C%20casual%20stylish%20outfit%2C%20friendly%20expression%2C%20warm%20studio%20lighting%2C%20cream%20background&width=120&height=120&seq=author-avatar-02&orientation=squarish',
    quote: '在这里，我的旅行故事能被真正热爱生活的人看到。每一次互动都让我感受到社区的温度。',
  },
  {
    name: '科技猎奇',
    role: '科技创作者',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20male%20tech%20reviewer%20portrait%2C%20smart%20casual%20look%2C%20friendly%20smile%2C%20warm%20studio%20lighting%2C%20cream%20background&width=120&height=120&seq=author-avatar-07&orientation=squarish',
    quote: '平台的AI推荐算法非常精准，我的科技评测内容总能触达到最感兴趣的观众群体。',
  },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          {/* Brand Introduction */}
          <section className="mt-6 mb-14">
            <div className="text-center mb-10">
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-4">
                关于蜜桃视频
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                精选短视频内容平台，致力于为全球用户提供优质、多元、创新的短视频内容体验，
                让每一位创作者和观众都能在这里找到属于自己的精彩。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              <div className="p-6 rounded-xl bg-background-100 text-center">
                <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-heart-line text-accent-600 text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">5000万+</h3>
                <p className="text-sm text-foreground-500">月活跃用户</p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 text-center">
                <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-vidicon-line text-primary-600 text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">100万+</h3>
                <p className="text-sm text-foreground-500">活跃创作者</p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 text-center">
                <div className="w-14 h-14 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-play-circle-line text-secondary-600 text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">2亿+</h3>
                <p className="text-sm text-foreground-500">日视频播放量</p>
              </div>
            </div>

            <div className="rounded-xl bg-background-100 p-6 md:p-8">
              <h2 className="font-heading text-xl text-foreground-900 mb-4">品牌故事</h2>
              <div className="text-sm text-foreground-600 leading-relaxed space-y-3">
                <p>
                  蜜桃视频诞生于2018年，创始团队怀着"让每个人都能发现生活的美好"的初心，打造了这个专注于优质短视频内容的平台。
                  在信息爆炸的时代，我们坚持内容质量为王，通过严格的审核机制和智能推荐算法，为用户甄选最有价值的视频内容。
                </p>
                <p>
                  我们相信，好的内容值得被更多人看到。蜜桃视频不仅是一个观看平台，更是一个充满活力的创作社区。
                  无论你是美食达人、旅行爱好者、音乐才子还是科技极客，这里都有属于你的舞台和观众。
                </p>
                <p>
                  多年来，蜜桃视频始终秉持"以创作者为中心，以用户体验为导向"的理念，持续投入技术创新和内容生态建设。
                  从AI智能推荐到创作辅助工具，从创作者激励计划到品牌合作机会，我们不断为创作者和用户创造更多价值。
                </p>
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="mb-14">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 text-center mb-8">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex items-start gap-6 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="hidden md:block w-1/2"></div>
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 -translate-x-1/2 mt-1.5 ring-4 ring-background-50"></div>
                    <div className="ml-10 md:ml-0 md:w-1/2 md:px-8">
                      <div className="p-5 rounded-xl bg-background-100">
                        <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-2 whitespace-nowrap">
                          {item.year}
                        </span>
                        <h4 className="font-heading text-base text-foreground-900 mb-1">{item.title}</h4>
                        <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-14">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 text-center mb-8">
              创作者口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.name} className="p-6 rounded-xl bg-background-100">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-12 h-12 rounded-full object-cover"
                      loading="lazy"
                    />
                    <div>
                      <h4 className="font-heading text-sm text-foreground-900 whitespace-nowrap">{t.name}</h4>
                      <p className="text-xs text-secondary-500">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed italic">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 text-center mb-8">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="p-6 rounded-xl bg-background-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-mail-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-sm text-foreground-900">电子邮箱</h4>
                    <p className="text-xs text-foreground-500">工作日24小时内回复</p>
                  </div>
                </div>
                <a href="mailto:support@mitaovideo.com" className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap">
                  support@mitaovideo.com
                </a>
              </div>
              <div className="p-6 rounded-xl bg-background-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className="ri-phone-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-sm text-foreground-900">客服热线</h4>
                    <p className="text-xs text-foreground-500">工作日 9:00-18:00</p>
                  </div>
                </div>
                <a href="tel:400-888-9999" className="text-sm text-accent-600 hover:text-accent-700 font-medium whitespace-nowrap">
                  400-888-9999
                </a>
              </div>
              <div className="p-6 rounded-xl bg-background-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center">
                    <i className="ri-map-pin-line text-secondary-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-sm text-foreground-900">公司地址</h4>
                    <p className="text-xs text-foreground-500">欢迎来访洽谈合作</p>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  北京市海淀区中关村科技园区创新路88号蜜桃大厦
                </p>
              </div>
              <div className="p-6 rounded-xl bg-background-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-wechat-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-sm text-foreground-900">官方微信</h4>
                    <p className="text-xs text-foreground-500">扫码关注获取最新资讯</p>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 whitespace-nowrap">
                  微信公众号：蜜桃视频官方
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}